import i18next from '../i18n';
import { toast } from 'react-toastify';
import { useRef, useLayoutEffect, useState } from 'react';
import { buildErrorTranslationParams } from './messages/errors/errorTranslationParams';

export const getOnlyAttributeUpdated = (base, updated) => {
  let baseEntries = Object.entries(base);
  let updatedEntries = Object.entries(updated);
  let toUpdate = [];
  updatedEntries.forEach((e, i) => {
    if (
      Array.isArray(e[1]) &&
      !e[1].length &&
      Array.isArray(baseEntries[i][1]) &&
      !baseEntries[i][1].length
    ) {
      return;
    }

    if (e[1] !== undefined && e[1] !== baseEntries[i][1]) {
      toUpdate.push(e);
    }
  });

  return Object.fromEntries(toUpdate);
};

export const getOnlyAttributeUpdatedBetweenObjects = (base, updated) => {
  for (let [key, value] of Object.entries(updated)) {
    if (base[key] === value) {
      delete updated[key];
    }
  }
  return updated;
};

export const getNewKeyBetweenObjects = (base, updated) => {
  let baseKeys = Object.keys(base);
  let updatedKeys = Object.keys(updated);

  const updatedKey = updatedKeys.filter((updatedKey) => !baseKeys.includes(updatedKey));
  if (updatedKey.length === 1) {
    return { [updatedKey[0]]: parseFloat(updated[updatedKey[0]]) };
  }
};

// to use when there is only one key to update at a time
export const getNewKeyBetweenObjectsAnyValueType = (base, updated) => {
  let baseKeys = Object.keys(base);
  let updatedKeys = Object.keys(updated);

  if (updatedKeys.length !== baseKeys.length + 1) {
    return null;
  }

  const updatedKey = updatedKeys.find((updatedKey) => !baseKeys.includes(updatedKey));
  return { [updatedKey]: updated[updatedKey] };
};

export const checkObjectsEquality = (object1, object2) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (object1[key] !== object2[key]) {
      return false;
    }
  }

  return true;
};

export const displayErrorMessage = (selectedMessage, additionnalInfo = null) => {
  return i18next.t(
    `errors.${selectedMessage}`,
    buildErrorTranslationParams(selectedMessage, additionnalInfo),
  );
};

export const displayMessage = (selectedMessage, additionnalInfo) => {
  const text = i18next.t(`loaders.${selectedMessage}`);
  return additionnalInfo ? `${text} (${additionnalInfo})` : text;
};

export const getTotalSurfaceParcelGroup = (base, children) => {
  children.forEach((c) => {
    base += c.surface;
  });
  return base.toFixed(2);
};

/**
 * Add additionnal controls for input type="number"
 * because of some inadequate behavior on Firefox and chrome especially on decimal separator
 */
export const checkNumericOnInputNumber = (e) => {
  // Array of allowed key press on input number in addition of numbers
  const arrayAllowedKeyPress = [
    'Backspace',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'Delete',
    '-',
    '.',
    ',',
    'Tab',
  ];
  // Check the type of the target Input
  if (
    e.target.dataset.type === 'number' ||
    e.target.dataset.type === 'digit' ||
    e.target.dataset.type === 'negativeNumber'
  ) {
    if (arrayAllowedKeyPress.includes(e.key)) {
      return;
    }
    // Doing nothing if the value is not a number
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  }
};

/**
 * Check input number value format to have 2 decimals digits at most
 * And only ',' & '.' for decimal separator
 */
export const checkDecimalRegexTwoDigits = (e) => {
  if (e.target.dataset.type === 'number' || e.target.dataset.type === 'negativeNumber') {
    // Force user input behavior tranform ',' => '.' in input datatype number
    if (e.target.value.includes(',')) {
      e.target.value = e.target.value.replace(/,/g, '.');
    }
    if (
      e.target.dataset.type === 'number' &&
      !e.target.value.toString().match(/^[0-9]*[.]?[0-9]{0,2}$/)
    ) {
      return false;
    }
    if (
      (e.target.dataset.type === 'negativeNumber' &&
        !e.target.value.toString().match(/^[0]?[-]?[0-9]*[.]?[0-9]{0,2}$/)) ||
      e.target.value[0] === '.' ||
      e.target.value.includes('-.')
    ) {
      return false;
    }
  }
  return true;
};

const formatFunctionChecking = (value, regex) => {
  if (!isNaN(value)) {
    value = value.toString();
  }
  if (value.includes(',')) {
    value = value.replace(/,/g, '.');
  }
  if (value[0] === '0' && value.length > 1 && value[1] !== '.') {
    value = value.substring(1);
  }
  return regexValidator(value, regex);
};

const regexValidator = (value, regex) => {
  if (typeof value !== 'string') {
    return value;
  }

  // Normalize different aspostrophe  [‘, ’, ‛, ′] => '
  const normalized = value.replace(/[\u2018\u2019\u201B\u2032]/g, "'");

  if (normalized.match(regex)) {
    return normalized;
  }

  return normalized.slice(0, -1);
};

export const checkTypeStringRegexDatagrid = (value) => {
  const regex =
    /^[a-zA-Z0-9àáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.\'\-_\(\)]+$/;
  return regexValidator(value, regex);
};

export const checkEmptyFieldAndDecimalRegexDatagridValue = (value, decimal) => {
  const regex = new RegExp(`^[0]?[0-9]*[.]?[0-9]{0,${decimal}}$`);
  if (value.length === 0 || !value.length) {
    value = '0';
  }
  return formatFunctionChecking(value, regex);
};
export const checkDecimalRegexTwoDigitsDatagridValue = (value) => {
  const regex = /^[0]?[0-9]*[.]?[0-9]{0,2}$/;
  return formatFunctionChecking(value, regex);
};
export const checkEmptyFieldAndNoDecimalDatagridValue = (value) => {
  const regex = /^[0]?[0-9]*[.]?[0-9]{0,2}$/;
  if (value.length === 0 || !value.length) {
    value = '0';
  }
  return formatFunctionChecking(value, regex);
};
export const checkThreeValueAndDecimalRegexTwoDigitsDatagrid = (value) => {
  const regex = /^[0]?[0-9]{0,3}(\.[.]?[0-9]{0,2})?$/;
  return formatFunctionChecking(value, regex);
};
export const checkNoDecimalRegexTwoDigitsDatagridValue = (value) => {
  const regex = /^[0]?[0-9]*?[0-9]{0}$/;
  return formatFunctionChecking(value, regex);
};

export const checkDecimalNegativeDatagrid = (value) => {
  const regex = /^[0]?[-]?[0-9]*[.]?[0-9]{0,2}$/;
  return formatFunctionChecking(value, regex);
};

export const checkEmptyFieldAndNoDecimalDatagridInteger = (value) => {
  const regex = /^[0]?[0-9]*?[0-9]{0}$/;

  if (value.length === 0 || !value.length) {
    value = '0';
  }
  if (value[0] === '0' && value.length > 1) {
    value = value.substring(1);
  }

  if (typeof value === 'string' && value.toString().match(regex)) {
    return parseInt(value);
  } else {
    value.substring(value.length - 1, 0);
    return parseInt(value);
  }
};

/**
 * Only accept digits
 */
export const checkDigitRegex = (e) => {
  if (e.target.dataset.type === 'digit') {
    if (!e.target.value.toString().match(/^[0-9]*$/)) {
      return false;
    }
  }
  return true;
};

/**
 * '0' is set on emptied field.
 * When field is filled, initial '0' is removed
 */
export const emptyFieldToZero = (value) => {
  if (value === '') {
    value = 0;
  }
  if (value[0] === '0' && value.length > 1 && value[1] !== '.') {
    value = value.substring(1);
  }
  return value;
};

//Used to format import files size
export const formatFileSize = (size) => {
  let gb = Math.trunc(size / 1000000000);
  let mb = Math.trunc(size / 1000000);
  let kb = Math.trunc(size / 1000);
  let b = Math.trunc(size % 1000);
  kb = kb > 9 ? kb - 10 * Math.trunc(kb / 10) : kb;
  mb = mb > 9 ? mb - 10 * Math.trunc(mb / 10) : mb;
  gb = gb > 9 ? gb - 10 * Math.trunc(gb / 10) : gb;

  if (size < 1000) {
    return `${b} octet${b > 1 ? 's' : ''}`;
  }

  return `${gb > 0 ? gb + 'go ' : ''} ${mb > 0 ? mb + 'mo ' : ''} ${kb > 0 ? kb + 'ko ' : ''}`;
};

export const getStringInsideParentheses = (string) => {
  return string.match(/\(([^)]+)\)/)?.[1];
};

//Used to calculate percentage relative to a total
export const getPercentage = (number, total) => {
  number = parseFloat(number);
  total = parseFloat(total);
  if (total === 0) {
    return i18next.t('errors.divisionByZero');
  }

  return ((number / total) * 100).toFixed(0);
};

export const getColorAverageBiomassInput = (averageBiomassInput) => {
  return averageBiomassInput < 15
    ? 'red'
    : averageBiomassInput >= 15 && averageBiomassInput < 20
      ? 'orange'
      : averageBiomassInput >= 20
        ? 'green'
        : 'black';
};

export const getConstraintsCultures = (index) => {
  if (
    !location.state ||
    !location.state.maxs ||
    !location.state.mins ||
    !location.state.maxsPct ||
    !location.state.minsPct
  ) {
    return { min: 0, max: 0, type: 'undefined' };
  }

  let max = 0;
  let min = 0;
  let type = 'undefined';

  if (location.state.maxs[index] !== 0 || location.state.mins[index] !== 0) {
    max = location.state.maxs[index] || 0;
    min = location.state.mins[index] || 0;
    type = 'ha';
  } else if (location.state.maxsPct[index] !== 0 || location.state.minsPct[index] !== 0) {
    max = location.state.maxsPct[index] || 0;
    min = location.state.minsPct[index] || 0;
    type = '%';
  }

  return { min, max, type };
};

export const getParcelOrganicStateAtYear = (parcel, year) => {
  if (parcel === null || year === null) {
    return 'unknown';
  }
  let parcelCultureAtYear = null;
  if (parcel?.previousCulture1?.year === year) {
    parcelCultureAtYear = parcel?.previousCulture1;
  }
  if (parcel?.previousCulture2?.year === year) {
    parcelCultureAtYear = parcel.previousCulture2;
  }
  for (let i = 0; i < parcel?.cultures.length; i++) {
    if (parcel?.cultures[i]?.year === year) {
      parcelCultureAtYear = parcel?.cultures[i][0];
    }
  }
  return parcelCultureAtYear?.organicState;
};

export const checkLocalStorageKey = (key) => localStorage.getItem(key) !== null;

export const setInitialStateSwitchIsOrganic = (hasConventionalData) => {
  if (!checkLocalStorageKey('isOrganic') && hasConventionalData !== null) {
    localStorage.setItem('isOrganic', !hasConventionalData);
  }
};

export const removeValueFromArray = (array, value) => {
  if (!array || !Array.isArray(array)) {
    return [];
  }

  return array.filter((item) => item !== value);
};

/**
 * Universal sorting function using localeCompare
 * @param {Array} array - The array to sort
 * @param {string|null} key - The object key to use for sorting (null for string arrays)
 * @param {Object} options - Options for localeCompare and sort configuration
 * @returns {Array} - The sorted array
 */
export function sortWithLocale(array, key = null, additionalOptions = {}) {
  const options = {
    numeric: true,
    sensitivity: 'base',
    ignorePunctuation: true,
    ascending: true,
    ...additionalOptions,
  };

  if (array === undefined || array === null) {
    return [];
  }

  if (array.length === 0) return [];

  // Create a copy to avoid modifying the original
  const arrayToSort = [...array];

  arrayToSort.sort((a, b) => {
    let valueA, valueB;
    if (key) {
      valueA = getNestedValue(a, key);
      valueB = getNestedValue(b, key);
    } else {
      valueA = a;
      valueB = b;
    }

    // Check if values are objects
    if (typeof valueA === 'object' || typeof valueB === 'object') {
      console.error(
        'Error: Trying to sort objects without specifying a key. Please provide a key parameter for function sortWithLocale.',
      );
      return 0;
    }

    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1;
    if (valueB == null) return -1;

    valueA = String(valueA);
    valueB = String(valueB);
    const result = valueA.localeCompare(valueB, 'fr', options);

    // Reverse if descending sort
    return options?.ascending ? result : -result;
  });

  return arrayToSort;
}

/**
 * Helper function to access nested properties
 * @param {Object} obj - The source object
 * @param {string} path - The path to the property (e.g., "user.name")
 * @returns {null | any} - The property value
 */
export function getNestedValue(obj, path) {
  return path
    .split('.')
    .reduce((current, key) => (current && current[key] !== undefined ? current[key] : null), obj);
}

export const getHeightToSubtractFromRef = (element) => {
  if (!element) {
    console.warn('getHeightToSubtract: Aucun élément fourni');
    return 200;
  }

  try {
    const rect = element.getBoundingClientRect();
    const distanceFromTop = rect.top + window.scrollY;
    const safetyMargin = 24;

    return Math.round(distanceFromTop + safetyMargin);
  } catch (error) {
    console.error('Erreur dans getHeightToSubtract:', error);
    return 200;
  }
};

export const useCalculatedHeight = () => {
  const ref = useRef(null);
  const [height, setHeight] = useState('70%');

  useLayoutEffect(() => {
    if (ref.current) {
      const calculatedHeight = getHeightToSubtractFromRef(ref.current);
      setHeight(`calc(100vh - ${calculatedHeight}px)`);
    }
  }, []);

  return { ref, height };
};

export function exportFileAsCSV(fileToDownload, nameCSV) {
  try {
    const file = new Blob([fileToDownload.data], { type: 'text/csv' });
    const fileURL = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = nameCSV + '.csv';
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    toast.success(i18next.t('success.fileDownloaded'));
  } catch (error) {
    toast.error(i18next.t('errors.fileDownloadFailed'));
  }
}
export function sortByLocalName(array, compareVariable = 'name') {
  return array.sort((a, b) => a[compareVariable].localeCompare(b[compareVariable]));
}
